import React from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Server,
  Trash2,
} from "lucide-react";
import type {
  InventoryItemResponse,
  SspComponentResponse,
} from "../../../../api/frameworksApi";
import {
  FormField,
  FormSelect,
  FormTextarea,
} from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import { toddmmyyyy } from "../constants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { getPageWindow } from "../utils/pagination";

export interface InventoryTabProps {
  inventoryLoading: boolean;
  inventoryItems: InventoryItemResponse[];
  pagInventory: InventoryItemResponse[];
  filtInventory: InventoryItemResponse[];
  editingInventoryId: string | null;
  setEditingInventoryId: React.Dispatch<React.SetStateAction<string | null>>;
  editInventoryForm: {
    description: string;
    componentId: string;
    identifiers: { identifierType: string; identifierValue: string }[];
  };
  setEditInventoryForm: React.Dispatch<
    React.SetStateAction<{
      description: string;
      componentId: string;
      identifiers: { identifierType: string; identifierValue: string }[];
    }>
  >;
  savingInventoryItem: boolean;
  handleUpdateInventoryItem: () => void;
  sspComponents: SspComponentResponse[];
  isPublishedSsp: boolean;
  requestDelete: (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => void;
  handleDeleteInventoryItem: (itemId: string) => Promise<void>;
  tabPage: number;
  tabPageSize: number;
  setCurrentTabPagination: (pagination: {
    page: number;
    pageSize: number;
  }) => void;
}

export function InventoryTab({
  inventoryLoading,
  inventoryItems,
  pagInventory,
  filtInventory,
  editingInventoryId,
  setEditingInventoryId,
  editInventoryForm,
  setEditInventoryForm,
  savingInventoryItem,
  handleUpdateInventoryItem,
  sspComponents,
  isPublishedSsp,
  requestDelete,
  handleDeleteInventoryItem,
  tabPage,
  tabPageSize,
  setCurrentTabPagination,
}: InventoryTabProps) {
  return (
    <>
      {inventoryLoading ? (
        <LoadingSpinner text="Loading inventory" />
      ) : inventoryItems.length > 0 ? (
        <>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="divide-y divide-border">
              {pagInventory.map((item) => (
                <div key={item.id} className="px-5 py-3.5 hover:bg-muted/40 transition-colors">
                  {editingInventoryId === item.id ? (
                    <div className="space-y-3">
                      <FormField label="Description" required>
                        <FormTextarea
                          value={editInventoryForm.description}
                          onChange={(v) => setEditInventoryForm((f) => ({ ...f, description: v }))}
                          placeholder="Asset description"
                        />
                      </FormField>
                      <FormField label="Component">
                        <FormSelect
                          options={sspComponents.map((c) => ({ label: c.title, value: c.id }))}
                          value={editInventoryForm.componentId}
                          onChange={(v) => setEditInventoryForm((f) => ({ ...f, componentId: v }))}
                          placeholder="Select component"
                        />
                      </FormField>
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => setEditingInventoryId(null)}
                          className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateInventoryItem}
                          disabled={!editInventoryForm.description || savingInventoryItem}
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                          {savingInventoryItem ? "Saving" : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground break-words">{item.description}</p>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {item.componentName && (
                            <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 capitalize">
                              {item.componentName}
                            </span>
                          )}
                          {item.assetIdentifiers.map((ai) => {
                            const key = ai.identifierType.toLowerCase();
                            const tint =
                              /host|fqdn|dns/.test(key)
                                ? "bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400"
                                : /ip|network|address/.test(key)
                                  ? "bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400"
                                  : /type|category|class/.test(key)
                                    ? "bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400"
                                    : /mac/.test(key)
                                      ? "bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400"
                                      : /uuid|id/.test(key)
                                        ? "bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-500/10 dark:border-violet-500/20 dark:text-violet-400"
                                        : "bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-500/10 dark:border-slate-500/20 dark:text-slate-300";
                            return (
                              <span
                                key={ai.id}
                                className={`inline-flex items-start gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-mono max-w-full ${tint}`}
                              >
                                <span className="font-semibold tracking-wide text-[10px] flex-shrink-0 capitalize opacity-80">
                                  {ai.identifierType.replace(/[_-]/g, " ")}:
                                </span>
                                <span className="break-all min-w-0">
                                  {ai.identifierValue}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                        <Calendar className="size-3.5" />
                        <span>{toddmmyyyy(item.updatedAt || item.createdAt)}</span>
                      </div>
                      {!isPublishedSsp && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Tip label="Edit Inventory Item" side="bottom">
                            <button
                              onClick={() => {
                                setEditingInventoryId(item.id);
                                setEditInventoryForm({
                                  description: item.description,
                                  componentId: item.componentId || "",
                                  identifiers: item.assetIdentifiers.map((ai) => ({
                                    identifierType: ai.identifierType,
                                    identifierValue: ai.identifierValue,
                                  })),
                                });
                              }}
                              className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                            >
                              <Pencil className="size-3.5 hover:text-indigo-600" />
                            </button>
                          </Tip>
                          <Tip label="Delete Inventory Item" side="bottom">
                            <button
                              onClick={() => requestDelete("Inventory Item", item.id, item.description, () => handleDeleteInventoryItem(item.id))}
                              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="size-3.5 hover:text-red-600" />
                            </button>
                          </Tip>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {filtInventory.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((tabPage - 1) * tabPageSize + 1, filtInventory.length)}–{Math.min(tabPage * tabPageSize, filtInventory.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {filtInventory.length}
                </span>
              </p>
              {Math.ceil(filtInventory.length / tabPageSize) > 1 && (
                <div className="flex items-center gap-0.5">
                  <button
                    disabled={tabPage === 1}
                    onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="size-3" />
                  </button>

                  {getPageWindow(tabPage, Math.ceil(filtInventory.length / tabPageSize)).map((p, idx) =>
                    p === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                            ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                            : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                          }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    disabled={tabPage >= Math.ceil(filtInventory.length / tabPageSize)}
                    onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtInventory.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="size-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
          <Server className="size-8 text-muted-foreground mb-3 mx-auto" />
          <p className="text-sm font-medium">No inventory items</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add assets to the system inventory
          </p>
        </div>
      )}
    </>
  );
}
